import { FunctionComponent, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Folder, ArrowRight, ArrowLeft, Import, Users, HelpCircle } from 'lucide-react';
import Footer from './Footer';

interface MenuItemProps {
    to: string;
    icon: React.ReactNode;
    label: string;
    isActive?: boolean;
}

/**
 * Individual menu item component with navigation and accessibility features
 * @param to - Route path for navigation
 * @param icon - Icon element to display
 * @param label - Label text for the menu item
 * @param isActive - Whether this menu item is currently active
 */
const MenuItem: FunctionComponent<MenuItemProps> = ({
    to,
    icon,
    label,
    isActive = false
}) => (
    <li>
        <Link
            to={to}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isActive
                ? 'bg-blue-100 text-blue-700 font-medium'
                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                }`}
            aria-current={isActive ? 'page' : undefined}
        >
            <span className="translate-y-[-1px]" aria-hidden="true">
                {icon}
            </span>
            <span>{label}</span>
        </Link>
    </li>
);

/**
 * Main navigation menu component for the Djehooty application
 * Provides navigation controls and main menu items with accessibility support
 */
const Menu: FunctionComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const handleGoBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const handleGoForward = useCallback(() => {
        navigate(+1);
    }, [navigate]);

    const navigationButtonClass = "grid h-10 w-32 mb-2 place-content-center rounded-lg bg-gray-100 cursor-pointer hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors";

    const menuItems = [
        { to: '/documents', icon: <Folder color="black" size={18} aria-hidden="true" />, label: 'Documents' },
        { to: '/individuals', icon: <Users color="black" size={18} aria-hidden="true" />, label: 'Individuals' },
        { to: '/import', icon: <Import color="black" size={18} aria-hidden="true" />, label: 'Import' },
        { to: '/help', icon: <HelpCircle color="black" size={18} aria-hidden="true" />, label: 'Help' },
    ];

    return (
        <div className="px-2 py-2 flex flex-col h-full">
            {/* Navigation controls */}
            <div className="flex items-center gap-2 justify-center">
                <button
                    onClick={handleGoBack}
                    aria-label="Go back"
                    title="Go back"
                    className={navigationButtonClass}
                >
                    <ArrowLeft color="black" size={25} />
                </button>

                <button
                    onClick={handleGoForward}
                    aria-label="Go forward"
                    title="Go forward"
                    className={navigationButtonClass}
                >
                    <ArrowRight color="black" size={25} />
                </button>
            </div>

            {/* Main navigation menu */}
            <nav role="navigation" aria-label="Main menu">
                <ul className="space-y-1 text-base">
                    {menuItems.map((item) => (
                        <MenuItem
                            key={item.to}
                            to={item.to}
                            icon={item.icon}
                            label={item.label}
                            isActive={location.pathname === item.to}
                        />
                    ))}
                </ul>
            </nav>

            {/* Footer with version information */}
            <Footer />
        </div>
    );
}

export default Menu;